import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserRole } from "~/api/model/userRole";
import { useResolveShareToken } from "~/api/share/share";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
} from "~/api/users/users";
import shareBg from "~/assets/share-guest-login-illustration.png";
import { SplitLayout } from "~/components/layouts/SplitLayout";
import { GuestLoginForm } from "~/features/share/components/GuestLoginForm";
import { ShareAccessDenied } from "~/features/share/components/ShareAccessDenied";
import { ROUTES } from "~/helpers";

export const SharePage: FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const {
    data: shareData,
    isLoading: isResolving,
    isError: isTokenError,
  } = useResolveShareToken(token ?? "", {
    query: {
      enabled: !!token,
      retry: false,
    },
  });

  const { data: currentUser, isLoading: isCheckingAuth } = useGetCurrentUser();

  useEffect(() => {
    if ((currentUser || isLoggedIn) && shareData) {
      const shareDataWithChat = shareData as typeof shareData & {
        chatId?: string;
      };
      if (currentUser?.role === UserRole.GUEST) {
        navigate(
          ROUTES.CHAT_DOCUMENT({
            organizationId: shareData.organizationId!,
            caseId: shareData.caseId!,
            chatId: shareDataWithChat.chatId!,
          })
        );
      } else {
        navigate(
          ROUTES.CASE({
            organizationId: shareData.organizationId!,
            caseId: shareData.caseId!,
          })
        );
      }
    }
  }, [currentUser, isLoggedIn, shareData, navigate]);

  const handleLoginSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: getGetCurrentUserQueryKey(),
    });
    setIsLoggedIn(true);
  };

  if (isResolving || isCheckingAuth) {
    return (
      <SplitLayout>
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </SplitLayout>
    );
  }

  // Invalid or missing token
  if (!token || isTokenError || !shareData) {
    return (
      <SplitLayout>
        <ShareAccessDenied />
      </SplitLayout>
    );
  }

  // Already logged in - show loading while redirecting
  if (currentUser) {
    return (
      <SplitLayout>
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </SplitLayout>
    );
  }

  // Show guest login form
  return (
    <SplitLayout imageSrc={shareBg}>
      <GuestLoginForm
        email={shareData.email!}
        caseName={shareData.caseName}
        shareToken={token}
        onSuccess={handleLoginSuccess}
      />
    </SplitLayout>
  );
};
